import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import useRouterMonitoringSignalR from '@/hooks/useRouterMonitoringSignalR';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import {
  FaWifi,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaSync,
  FaExpand,
  FaCompress,
  FaClock,
  FaMapMarkerAlt,
  FaNetworkWired,
  FaArrowRight,
  FaGlobe,
  FaHeartbeat,
  FaSearch,
  FaTimes
} from 'react-icons/fa';
import './ConnectivityMonitoringPage.scss';
import * as Sentry from '@sentry/react';

/** Polling interval in milliseconds for the fallback data refresh. */
const POLL_INTERVAL = 30_000;

/**
 * Fixed sparkline history window in minutes.
 * 60 minutes gives enough context for the mini sparkline without overloading InfluxDB.
 * This value is intentionally not user-selectable on the wall-display dashboard —
 * it only affects the sparkline preview; the per-IP outage time (downSince) is computed
 * from a separate 7-day lookback and is NOT capped by this window.
 */
const SPARKLINE_HISTORY_MINUTES = 60;

/**
 * Connectivity Monitoring Dashboard
 *
 * Wall-display dashboard showing routers with public IP connectivity issues.
 * Auto-refreshes via SignalR real-time events + polling fallback.
 * Only shows routers with MonitorPublicIps enabled.
 *
 * Enhanced features:
 *  - Clickable severity stat chips to filter the card grid instantly
 *  - Real-time text search by router name or location
 *  - Sort options: Severity, Name, Location, Outage Duration
 *  - Countdown timer showing seconds until next auto-refresh
 *  - Min / Avg / Max fleet latency stats in the header
 *  - Outage duration badge colour-coded by severity (yellow → orange → red)
 *  - Alert badges colour-coded by type (offline=red, latency=blue, resource=purple)
 */
const ConnectivityMonitoringPage = () => {
  const { apiFetch } = useAuth();
  const navigate = useNavigate();

  // ── Core data state ──────────────────────────────────────────────────────
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [overview, setOverview]       = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [fullscreen, setFullscreen] = useState(false);
  const [showAll, setShowAll]       = useState(false);

  // ── Filter / sort state ───────────────────────────────────────────────────
  /**
   * Active severity filter chip.
   * null = default (issues-only unless showAll); otherwise one of:
   * 'critical' | 'warning' | 'healthy' | 'stale'
   */
  const [statusFilter, setStatusFilter] = useState(null);
  /** Text typed in the search bar — filters by router name or location. */
  const [searchTerm, setSearchTerm]     = useState('');
  /** Sort order applied to the visible card grid. */
  const [sortBy, setSortBy]             = useState('severity');

  // ── Countdown timer state ─────────────────────────────────────────────────
  /** Seconds remaining until the next automatic data refresh. */
  const [countdown, setCountdown] = useState(POLL_INTERVAL / 1000);

  const containerRef = useRef(null);
  const pollRef      = useRef(null);

  // ── Data fetching ─────────────────────────────────────────────────────────
  const fetchOverview = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await apiFetch(
        `/api/RouterConfig/connectivity-overview?historyMinutes=${SPARKLINE_HISTORY_MINUTES}`
      );
      const json = await res.json();
      if (json.success) {
        setOverview(json);
        setLastRefresh(new Date());
        setError(null);
      } else {
        setError(json.error || 'Failed to load connectivity data');
      }
    } catch (err) {
        Sentry.captureException(err);
      setError(err.message || 'Failed to load connectivity data');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  // Initial load on mount
  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  // Polling fallback
  useEffect(() => {
    pollRef.current = setInterval(() => fetchOverview(true), POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [fetchOverview]);

  // ── Countdown timer ───────────────────────────────────────────────────────
  // Tick every second.
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Reset countdown whenever a refresh completes.
  useEffect(() => {
    if (lastRefresh) setCountdown(POLL_INTERVAL / 1000);
  }, [lastRefresh]);

  // ── SignalR real-time updates ──────────────────────────────────────────────
  const handleAvailabilityChange = useCallback(() => {
    fetchOverview(true);
  }, [fetchOverview]);

  useRouterMonitoringSignalR({
    onRouterAvailabilityChanged: handleAvailabilityChange,
    enabled: true
  });

  // ── Fullscreen toggle ──────────────────────────────────────────────────────
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
      setFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handler = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // ── Filter / sort / display logic ─────────────────────────────────────────
  const routers = overview?.data || [];

  /**
   * Toggles a severity stat chip as the active filter.
   * Clicking the already-active chip clears the filter.
   *
   * @param {string} filter - 'critical' | 'warning' | 'healthy' | 'stale'
   */
  const handleStatClick = useCallback((filter) => {
    setStatusFilter(prev => prev === filter ? null : filter);
  }, []);

  /** Clears all active search text and severity filters. */
  const clearFilters = useCallback(() => {
    setStatusFilter(null);
    setSearchTerm('');
  }, []);

  /**
   * The filtered and sorted list of routers to render as cards.
   *
   * Filtering pipeline:
   *   1. Status filter (severity chip) or default issues-only / showAll
   *   2. Text search against name and location
   *   3. Sort by the selected criterion
   */
  const displayed = useMemo(() => {
    // Step 1: severity filter
    let list;
    if (statusFilter === 'critical') {
      list = routers.filter(r => r.allDown);
    } else if (statusFilter === 'warning') {
      list = routers.filter(r => r.anyDown && !r.allDown);
    } else if (statusFilter === 'healthy') {
      list = routers.filter(r => !r.anyDown);
    } else if (statusFilter === 'stale') {
      list = routers.filter(r => r.staleData);
    } else {
      // No explicit filter: respect showAll toggle
      list = showAll ? routers : routers.filter(r => r.anyDown);
    }

    // Step 2: text search (name or location, case-insensitive)
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      list = list.filter(r =>
        r.name.toLowerCase().includes(term) ||
        (r.location || '').toLowerCase().includes(term)
      );
    }

    // Step 3: sort
    const severityScore = r => (r.allDown ? 3 : r.anyDown ? 2 : r.staleData ? 1 : 0);
    return [...list].sort((a, b) => {
      if (sortBy === 'name')     return a.name.localeCompare(b.name);
      if (sortBy === 'location') return (a.location || '').localeCompare(b.location || '');
      if (sortBy === 'duration') return (b.outageDuration || 0) - (a.outageDuration || 0);
      // Default: severity descending, then name ascending
      const diff = severityScore(b) - severityScore(a);
      return diff !== 0 ? diff : a.name.localeCompare(b.name);
    });
  }, [routers, statusFilter, showAll, searchTerm, sortBy]);

  /** True when any non-default filter is active (status chip or search text). */
  const hasActiveFilters = !!statusFilter || !!searchTerm.trim();

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading && !overview) {
    return (
      <div className="connectivity-monitoring-page">
        <HexagonLoader />
      </div>
    );
  }

  return (
    <div
      className={`connectivity-monitoring-page${fullscreen ? ' fullscreen' : ''}`}
      ref={containerRef}
    >
      {/* ── Header ── */}
      <div className="cm-header">
        <div className="cm-header-left">
          <FaWifi className="cm-header-icon" />
          <h1>Connectivity Monitoring</h1>
        </div>

        {/* Clickable severity stat chips — click to filter the grid */}
        <div className="cm-header-stats">
          <div
            className={`cm-stat clickable ${overview?.routersAllDown > 0 ? 'critical' : ''} ${statusFilter === 'critical' ? 'active' : ''}`}
            onClick={() => handleStatClick('critical')}
            title="Click to filter: Full Outage"
            role="button"
            tabIndex={0}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleStatClick('critical')}
          >
            <span className="cm-stat-value">{overview?.routersAllDown ?? 0}</span>
            <span className="cm-stat-label">Full Outage</span>
          </div>
          <div
            className={`cm-stat clickable ${overview?.routersWithIssues > 0 ? 'warning' : ''} ${statusFilter === 'warning' ? 'active' : ''}`}
            onClick={() => handleStatClick('warning')}
            title="Click to filter: Partial Outage"
            role="button"
            tabIndex={0}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleStatClick('warning')}
          >
            <span className="cm-stat-value">{overview?.routersWithIssues ?? 0}</span>
            <span className="cm-stat-label">Partial</span>
          </div>
          <div
            className={`cm-stat clickable healthy ${statusFilter === 'healthy' ? 'active' : ''}`}
            onClick={() => handleStatClick('healthy')}
            title="Click to filter: Healthy"
            role="button"
            tabIndex={0}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleStatClick('healthy')}
          >
            <span className="cm-stat-value">
              {overview?.routersHealthy ?? ((overview?.totalRouters ?? 0) - (overview?.routersWithIssues ?? 0))}
            </span>
            <span className="cm-stat-label">Healthy</span>
          </div>
          {overview?.routersStale > 0 && (
            <div
              className={`cm-stat clickable stale ${statusFilter === 'stale' ? 'active' : ''}`}
              onClick={() => handleStatClick('stale')}
              title="Click to filter: Stale Data"
              role="button"
              tabIndex={0}
              onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleStatClick('stale')}
            >
              <span className="cm-stat-value">{overview.routersStale}</span>
              <span className="cm-stat-label">Stale Data</span>
            </div>
          )}
          {overview?.totalMonitoredIps > 0 && (
            <>
              <div className="cm-stat-separator" />
              <div className={`cm-stat ${overview?.ipsDown > 0 ? 'warning' : 'healthy'}`}>
                <span className="cm-stat-value">{overview?.ipsUp ?? 0}/{overview?.totalMonitoredIps ?? 0}</span>
                <span className="cm-stat-label">IPs Up</span>
              </div>
            </>
          )}
          {overview?.fleetLatency?.avgMs != null && (
            <>
              <div className="cm-stat-separator" />
              {/* Min latency is included in the API response but was previously hidden */}
              {overview.fleetLatency.minMs != null && (
                <div className="cm-stat">
                  <span className="cm-stat-value">{overview.fleetLatency.minMs}ms</span>
                  <span className="cm-stat-label">Min Latency</span>
                </div>
              )}
              <div className="cm-stat">
                <span className="cm-stat-value">{overview.fleetLatency.avgMs}ms</span>
                <span className="cm-stat-label">Avg Latency</span>
              </div>
              <div className="cm-stat">
                <span className="cm-stat-value">{overview.fleetLatency.maxMs}ms</span>
                <span className="cm-stat-label">Max Latency</span>
              </div>
            </>
          )}
        </div>

        {/* Header right: action buttons + countdown timer */}
        <div className="cm-header-right">
          <button onClick={() => fetchOverview(true)} className="cm-btn" title="Refresh now">
            <FaSync />
          </button>
          <button onClick={toggleFullscreen} className="cm-btn" title="Toggle fullscreen">
            {fullscreen ? <FaCompress /> : <FaExpand />}
          </button>
          {lastRefresh && (
            <span className="cm-last-refresh" title={`Last refreshed at ${lastRefresh.toLocaleTimeString()}`}>
              <FaClock /> {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          {/* Countdown to next auto-refresh — particularly useful on wall displays */}
          <span
            className={`cm-countdown${countdown <= 5 ? ' imminent' : ''}`}
            title="Seconds until next automatic refresh"
          >
            ↻ {countdown}s
          </span>
        </div>
      </div>

      {/* ── Controls bar: search, sort, show-all ── */}
      <div className="cm-controls-bar">
        {/* Text search: filter by router name or location */}
        <div className="cm-search-wrapper">
          <FaSearch className="cm-search-icon" />
          <input
            type="text"
            className="cm-search-input"
            placeholder="Search routers…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            aria-label="Search routers by name or location"
          />
          {searchTerm && (
            <button
              className="cm-search-clear"
              onClick={() => setSearchTerm('')}
              title="Clear search"
              aria-label="Clear search"
            >
              <FaTimes />
            </button>
          )}
        </div>

        {/* Sort order selector */}
        <select
          className="cm-select"
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          aria-label="Sort routers by"
          title="Sort order"
        >
          <option value="severity">Sort: Severity</option>
          <option value="name">Sort: Name</option>
          <option value="location">Sort: Location</option>
          <option value="duration">Sort: Duration</option>
        </select>

        {/* Show All toggle */}
        <label className="cm-toggle">
          <input
            type="checkbox"
            checked={showAll}
            onChange={e => setShowAll(e.target.checked)}
          />
          <span>Show All</span>
        </label>

        {/* Clear filters button — only visible when a filter is active */}
        {hasActiveFilters && (
          <button className="cm-btn" onClick={clearFilters} title="Clear all filters">
            <FaTimes /> Clear Filters
          </button>
        )}

        {/* Result count: "X / Y routers" */}
        <span className="cm-results-count">
          {displayed.length} / {routers.length} routers
        </span>
      </div>

      {error && (
        <div className="cm-error">
          <FaExclamationTriangle /> {error}
        </div>
      )}

      {/* ── Router cards ── */}
      <div className="cm-grid">
        {displayed.length === 0 && !error && (
          <div className="cm-empty">
            {hasActiveFilters ? (
              <>
                <FaSearch size={48} />
                <h2>No Matches</h2>
                <p>No routers match your current filters.</p>
                <button className="cm-btn-link" onClick={clearFilters}>
                  Clear filters
                </button>
              </>
            ) : (
              <>
                <FaCheckCircle size={64} />
                <h2>All Clear</h2>
                <p>All monitored IPs are responding normally.</p>
                {!showAll && routers.length > 0 && (
                  <button className="cm-btn-link" onClick={() => setShowAll(true)}>
                    Show all {routers.length} routers
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {displayed.map((router) => (
          <RouterCard
            key={router.routerId}
            router={router}
            onNavigate={() => navigate(`/it/config-management/routers/${router.routerId}`)}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Individual router connectivity card.
 *
 * Displays management IP, tunnel status, public IPs with sparklines, a trouble
 * summary with a colour-coded outage-duration badge, and active alert badges.
 * The card's left border and status dot reflect the overall router severity.
 *
 * @param {{ router: object, onNavigate: () => void }} props
 */
const RouterCard = ({ router, onNavigate }) => {
  const severity = router.allDown   ? 'critical'
    : router.staleData              ? 'stale'
    : router.anyDown                ? 'warning'
    : 'healthy';
  const monitoredIps = router.ips.filter(ip => ip.monitorIcmp);
  const mgmt = router.mgmtIp;

  return (
    <div className={`cm-card ${severity}`} onClick={onNavigate} title="Click to view router details">
      <div className="cm-card-header">
        <div className="cm-card-title">
          <span className={`cm-status-dot ${severity}`} />
          <h3>{router.name}</h3>
        </div>
        {router.location && (
          <span className="cm-card-location">
            <FaMapMarkerAlt /> {router.location}
          </span>
        )}
      </div>

      {/* Management IP status */}
      <div className="cm-card-tunnel">
        <FaHeartbeat />
        <span>Mgmt IP: </span>
        {mgmt?.hasData ? (
          <>
            <span className={mgmt.up ? 'up' : 'down'}>
              {mgmt.up ? 'Up' : 'Down'}
            </span>
            {mgmt.up && mgmt.rtt != null && (
              <span className="cm-ip-rtt">{mgmt.rtt}ms</span>
            )}
          </>
        ) : (
          <span className="cm-ip-nodata">No data</span>
        )}
        <span className="cm-ip-addr">{mgmt?.ip || router.tunnelIp}</span>
        {mgmt?.history && mgmt.history.length > 1 && (
          <Sparkline data={mgmt.history} />
        )}
      </div>

      {/* Tunnel / WireGuard status */}
      <div className="cm-card-tunnel">
        <FaNetworkWired />
        <span>Tunnel: </span>
        {router.tunnelStale ? (
          <span className="stale">Stale</span>
        ) : (
          <span className={router.tunnelUp ? 'up' : 'down'}>
            {router.tunnelUp ? 'Up' : 'Down'}
          </span>
        )}
        {router.lastIcmpSuccess && (
          <span className="cm-card-ago">
            last: {formatAgo(router.lastIcmpSuccess)}
          </span>
        )}
      </div>

      {/* Public IPs */}
      <div className="cm-card-ips">
        {monitoredIps.length > 0 && (
          <div className="cm-ip-section-label"><FaGlobe /> Public IPs</div>
        )}
        {monitoredIps.map((ip) => (
          <div key={ip.id} className={`cm-ip-row ${ip.up ? 'up' : 'down'}`}>
            <span className={`cm-ip-dot ${ip.up ? 'up' : 'down'}`} />
            <span className="cm-ip-label">{ip.label}</span>
            <span className="cm-ip-addr">{ip.ip}</span>
            {ip.up && ip.rtt != null && (
              <span className="cm-ip-rtt">{ip.rtt}ms</span>
            )}
            {!ip.up && ip.hasData && (
              <span className="cm-ip-error">
                {ip.error ? truncate(ip.error, 40) : 'No response'}
              </span>
            )}
            {!ip.up && ip.downSince && (
              <span className="cm-ip-down-since" title={`Down since ${new Date(ip.downSince).toLocaleString()}`}>
                <FaClock /> {formatDownSince(ip.downSince)}
              </span>
            )}
            {!ip.hasData && (
              <span className="cm-ip-nodata">No data</span>
            )}
            {ip.history && ip.history.length > 1 && (
              <Sparkline data={ip.history} />
            )}
          </div>
        ))}
        {monitoredIps.length === 0 && (
          <div className="cm-ip-row no-monitoring">
            <em>No IPs monitored</em>
          </div>
        )}
      </div>

      {/* Trouble summary with colour-coded outage duration */}
      {router.anyDown && (
        <div className="cm-card-trouble">
          <FaExclamationTriangle />
          {router.allDown
            ? <span>All public IPs unreachable — possible full outage</span>
            : router.staleData
            ? <span>Monitoring data is stale — status unknown</span>
            : <span>{monitoredIps.filter(ip => !ip.up).length} of {monitoredIps.length} IPs down</span>
          }
          {router.outageDuration > 0 && (
            <span className={`cm-outage-duration ${getOutageDurationClass(router.outageDuration)}`}>
              <FaClock /> {formatDuration(router.outageDuration)}
            </span>
          )}
          <FaArrowRight className="cm-card-arrow" />
        </div>
      )}

      {/* Active alerts — colour-coded by alert type */}
      {router.activeAlerts?.length > 0 && (
        <div className="cm-card-alerts">
          {router.activeAlerts.map((alert, index) => {
            const label = {
              Availability:        'Offline',
              PacketLossThreshold: 'Packet Loss',
              RttThreshold:        'High Latency',
              CpuThreshold:        'High CPU',
              MemoryThreshold:     'High Memory'
            }[alert.alertType] || alert.alertType;
            const count = alert.count > 1 ? ` (×${alert.count})` : '';
            // Colour the badge by alert category for quick visual triage.
            const badgeClass = {
              Availability:        'critical',
              PacketLossThreshold: 'warning',
              RttThreshold:        'latency',
              CpuThreshold:        'resource',
              MemoryThreshold:     'resource'
            }[alert.alertType] || 'critical';
            return (
              <span
                key={index}
                className={`cm-alert-badge ${badgeClass}`}
                title={`Since ${new Date(alert.createdDate).toLocaleString()}${alert.count > 1 ? ` — ${alert.count} occurrences` : ''}`}
              >
                {label}{count}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Tiny SVG sparkline showing up/down + latency over time.
 *
 * Down periods are rendered as translucent red bars behind the RTT trend line.
 * Only the last 30 data points are used to keep the chart readable at small sizes.
 *
 * @param {{ data: Array<{ up: boolean, rtt: number|null }> }} props
 */
const Sparkline = ({ data }) => {
  const width   = 100;
  const height  = 20;
  const padding = 1;
  const points  = data.slice(-30);
  if (points.length < 2) return null;

  const maxRtt = Math.max(...points.map(p => p.rtt ?? 0), 1);
  const step   = (width - padding * 2) / (points.length - 1);

  const pathParts = points.map((p, index) => {
    const x   = padding + index * step;
    const rtt = p.rtt ?? 0;
    const y   = height - padding - ((rtt / maxRtt) * (height - padding * 2));
    return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
  });

  return (
    <svg className="cm-sparkline" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      {/* Down periods rendered as red background bars */}
      {points.map((p, index) => {
        if (p.up) return null;
        const x = padding + index * step;
        return (
          <rect
            key={index}
            x={x - step / 3}
            y={0}
            width={step * 0.6}
            height={height}
            className="cm-spark-down"
          />
        );
      })}
      {/* RTT trend line */}
      <path d={pathParts.join('')} className="cm-spark-line" />
    </svg>
  );
};

// ── Helper functions ──────────────────────────────────────────────────────────

/**
 * Returns a CSS modifier class for the outage-duration badge based on how long
 * the outage has lasted.
 *
 * @param {number} minutes - Outage duration in minutes.
 * @returns {string} '' | 'medium' | 'high'
 */
function getOutageDurationClass(minutes) {
  if (minutes >= 120) return 'high';
  if (minutes >= 30)  return 'medium';
  return '';
}

/**
 * Returns a human-readable "X ago" string for a given ISO date string.
 * Treats the input as UTC to account for backend dates serialized without
 * a timezone indicator (EF Core DateTime with Kind = Unspecified).
 *
 * @param {string} dateStr - ISO 8601 date string.
 * @returns {string}
 */
function formatAgo(dateStr) {
  if (!dateStr) return '';
  // Ensure UTC interpretation for backend dates that lack a 'Z' suffix
  const utcStr = typeof dateStr === 'string' && !dateStr.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(dateStr)
    ? dateStr + 'Z'
    : dateStr;
  const diff = Date.now() - new Date(utcStr).getTime();
  if (diff < 0)              return 'just now';
  if (diff < 60_000)         return '<1m ago';
  if (diff < 3_600_000)      return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000)     return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

/**
 * Returns a compact human-readable duration string from a UTC ISO timestamp
 * to now, e.g. "3m", "1h 12m", "2d 5h". Used for per-IP downtime indicators.
 *
 * @param {string} dateStr - ISO 8601 date string (UTC).
 * @returns {string}
 */
function formatDownSince(dateStr) {
  if (!dateStr) return '';
  const utcStr = typeof dateStr === 'string' && !dateStr.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(dateStr)
    ? dateStr + 'Z'
    : dateStr;
  const mins = (Date.now() - new Date(utcStr).getTime()) / 60_000;
  return formatDuration(mins);
}

/**
 * Formats a duration in minutes to a compact, human-readable string.
 *
 * @param {number} minutes - Duration in minutes.
 * @returns {string}
 */
function formatDuration(minutes) {
  if (minutes < 1)  return '<1m';
  if (minutes < 60) return `${Math.floor(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  const mins  = Math.floor(minutes % 60);
  if (hours < 24) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  const days    = Math.floor(hours / 24);
  const remainH = hours % 24;
  return remainH > 0 ? `${days}d ${remainH}h` : `${days}d`;
}

/**
 * Truncates a string to `len` characters, appending '…' if truncated.
 *
 * @param {string} str - The input string.
 * @param {number} len - Maximum allowed length.
 * @returns {string}
 */
function truncate(str, len) {
  return str.length > len ? str.slice(0, len) + '…' : str;
}

export default ConnectivityMonitoringPage;
